import { useTellescopeMutation } from "@tellescope/tanstack-query";
import type { ChatAttachment } from "@tellescope/types-models";
import { useEnduserStore } from "../store/hooks";

interface SendChatMessageParams {
  roomId: string;
  message: string;
  attachments?: ChatAttachment[];
}

/**
 * Hook for sending chat messages from the enduser portal.
 * Creates a new chat message in the specified room.
 */
export function useSendChatMessage() {
  const session = useEnduserStore((state) => state.session);
  const enduserId = useEnduserStore((state) => state.currentEnduser?.id);

  const mutation = useTellescopeMutation({
    model: "chats",
    method: "createOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "invalidate",
  });

  const sendMessage = ({ roomId, message, attachments }: SendChatMessageParams) => {
    if (!enduserId) {
      throw new Error("No enduser ID available");
    }

    return mutation.mutate({
      roomId,
      message,
      enduserId,
      senderId: enduserId,
      attachments,
    });
  };

  const sendMessageAsync = ({ roomId, message, attachments }: SendChatMessageParams) => {
    if (!enduserId) {
      throw new Error("No enduser ID available");
    }

    return mutation.mutateAsync({
      roomId,
      message,
      enduserId,
      senderId: enduserId,
      attachments,
    });
  };

  return {
    sendMessage,
    sendMessageAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}
